import { RefObject, useEffect, useState } from "react";

function findScrollableAncestor(element: Element): Element | null {
    let parent = element.parentElement;
    while (parent) {
        const style = getComputedStyle(parent);
        const overflow = style.overflowY;
        if (overflow === "scroll" || overflow === "auto") {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

/**
 * Returns true once the observed element is visible in its scrollable ancestor.
 * Disconnects after first trigger (one-shot).
 */
export function useInView(
    ref: RefObject<Element>,
    options?: { threshold?: number }
): boolean {
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            console.log("[useInView] ref.current is null");
            return;
        }

        const root = findScrollableAncestor(element);
        const threshold = options?.threshold ?? 1.0;
        console.log("[useInView] observing element:", element.tagName, element.className);
        console.log("[useInView] scrollable root:", root?.tagName, root?.className ?? "null (viewport)");
        console.log("[useInView] threshold:", threshold);

        const observer = new IntersectionObserver(
            ([entry]) => {
                console.log("[useInView] callback:", {
                    isIntersecting: entry.isIntersecting,
                    intersectionRatio: entry.intersectionRatio,
                    boundingClientRect: entry.boundingClientRect,
                    rootBounds: entry.rootBounds,
                });
                if (entry.isIntersecting) {
                    setInView(true);
                    observer.disconnect();
                }
            },
            {
                root,
                threshold,
            }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [ref, options?.threshold]);

    return inView;
}
